import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import ConfirmDialog from '../ConfirmDialog';
import { updateOrganisation } from '../../services/organisation.service';
import { useAuth } from '../../context/authContext';
import { useToast } from '../ui/use-toast';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';

export default function ApprovalProcessCard({
    approvalProcessType,
    restrictSubmitterAuth,
    onUpdated,
}) {

    const { user } = useAuth();
    const { toast } = useToast();

    const [processType, setProcessType] = useState(approvalProcessType || 'STANDARD');
    const [restrict, setRestrict] = useState(!!restrictSubmitterAuth);

    React.useEffect(() => {
        setProcessType(approvalProcessType || 'STANDARD');
        setRestrict(!!restrictSubmitterAuth);
    }, [approvalProcessType, restrictSubmitterAuth]);

    const isCustom = processType === 'CUSTOM';
    const isDirty = processType !== (approvalProcessType || 'STANDARD')
        || (isCustom && restrict !== !!restrictSubmitterAuth);

    const handleConfirm = async () => {
        const requestBody = {
            approvalProcessType: processType,
            // restrictSubmitterAuth is managed in Authorisation Settings for STANDARD mode.
            ...(isCustom ? { restrictSubmitterAuth: restrict } : {}),
        };

        const response = await updateOrganisation(user.organisationId, requestBody, user.token);
        if (response && response.status === 200) {
            toast({
                title: 'Success',
                description: 'Approval process settings updated',
                variant: 'success',
            });
            onUpdated && onUpdated();
        } else {
            const message = response?.data?.error?.message || 'Error updating approval process settings';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-row items-center justify-between">
                    <h3 className="my-0">Approval Process</h3>
                    <Badge variant={isCustom ? 'success' : 'muted'}>
                        {isCustom ? 'Custom Workflows' : 'Standard'}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent>
                <p className="text-sm text-gray-600">
                    Choose how new payments are approved for your organisation. Existing payments retain
                    their current approval plans.
                </p>

                <div className="flex flex-col gap-3 mt-6">
                    <button
                        type="button"
                        onClick={() => setProcessType('STANDARD')}
                        className={`text-left rounded-md border p-4 transition-colors ${processType === 'STANDARD' ? 'border-yavrio-green bg-yavrio-green/5' : 'border-slate-200 hover:bg-slate-50'}`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-semibold">Standard</span>
                            {processType === 'STANDARD' && <Badge variant="success">Selected</Badge>}
                        </div>
                        <p className="text-sm text-gray-500 mb-0 mt-1">
                            Uses individual user authorisation limits and the organisation’s required approval count.
                        </p>
                    </button>

                    <button
                        type="button"
                        onClick={() => setProcessType('CUSTOM')}
                        className={`text-left rounded-md border p-4 transition-colors ${processType === 'CUSTOM' ? 'border-yavrio-green bg-yavrio-green/5' : 'border-slate-200 hover:bg-slate-50'}`}
                    >
                        <div className="flex items-center justify-between">
                            <span className="font-semibold">Custom Workflows</span>
                            {processType === 'CUSTOM' && <Badge variant="success">Selected</Badge>}
                        </div>
                        <p className="text-sm text-gray-500 mb-0 mt-1">
                            Uses workflow tiers and assigned approvers. Individual user authorisation limits
                            do not apply to payments using a custom workflow.
                        </p>
                    </button>
                </div>

                {isCustom && (
                    <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3">
                        <p className="text-xs text-amber-800 mb-0">
                            While Custom is active, incoming payments without a valid workflow reference are
                            rejected. Make sure at least one workflow is published before switching.
                        </p>
                    </div>
                )}

                {isCustom && (
                    <div className="mt-6">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <label className="flex items-center text-sm text-gray-600">
                                        <Switch
                                            checked={restrict}
                                            onCheckedChange={() => setRestrict(!restrict)}
                                            className="mr-2"
                                        />
                                        Submitter cannot approve their own payment
                                    </label>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="text-xs max-w-xs">If enabled, the person who submitted the payment request cannot approve it.</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </div>
                )}

                <ConfirmDialog
                    title="Update Approval Process"
                    description={isCustom
                        ? 'New payments will follow your published workflow. Assigned users may receive approval emails and approve payments above their saved individual limits. Existing payments retain their current approval plans.'
                        : 'New payments will use individual user authorisation limits and the organisation’s required approval count. Existing payments retain their current approval plans.'}
                    cancel="Cancel"
                    action={isCustom && approvalProcessType !== 'CUSTOM' ? 'Enable custom workflows' : 'Update'}
                    handleAction={handleConfirm}
                >
                    <Button disabled={!isDirty} className="mt-6 yvr-button">
                        Save Changes
                    </Button>
                </ConfirmDialog>
            </CardContent>
        </Card>
    );
}
